import React, { useState, useEffect, useRef } from "react";
import Header from "../Header";
import Footer from "../Footer";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import $ from "jquery";
import "datatables.net-bs5";
import { format } from "date-fns";
import config from "../common/Config";
import { setTableWidths, getTableWidths } from "../../data/postAPi";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import "../../components/css/custom-filter.css";

const SalesOrderHistory = () => {
  const [salesDates, setSalesDates] = useState([null, null]);
  const [grandTotal, setGrandTotal] = useState("");
  const [currentGrandTotal, setCurrentGrandTotal] = useState("");
  const salesOrderRef = useRef(null);
  const accessToken = localStorage.getItem("accessToken");

  // 🔹 Search value state
  const [searchValue, setSearchValue] = useState("");

  // 🔹 Debounce function to avoid hitting API every keystroke
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Handle form submit, set date range and trigger getSalesOrderList immediately
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const [startDate, endDate] = salesDates;
    let formattedStartDate = startDate ? format(startDate, "MM/dd/yyyy") : "";
    let formattedEndDate = endDate ? format(endDate, "MM/dd/yyyy") : "";
    getSalesOrderList(formattedStartDate, formattedEndDate, searchValue);
  };

  // Fetch sales order list from API
  const getSalesOrderList = (startDate, endDate, search) => {
    const $table = $(salesOrderRef.current);

    if ($.fn.DataTable.isDataTable($table)) {
      $table.DataTable().destroy();
    }

    $table.DataTable({
      processing: true,
      serverSide: true,
      ajax: {
        url: `${config.API_URL}/order/history`,
        type: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        data: function (d) {
          return $.extend({}, d, {
            draw: d.draw,
            start: d.start,
            length: d.length,
            searchValue: search, // 🔹 now from parameter
            searchByFromdate: startDate,
            searchByTodate: endDate,
            authorization: `Bearer ${accessToken}`,
          });
        },
        dataSrc: function (json) {
          if (json && json.data && Array.isArray(json.data)) {
            setGrandTotal(json.total_grand_total);
            setCurrentGrandTotal(json.total_grand_total1);
          }
          return Array.isArray(json.data) ? json.data : [];
        },
      },
      language: {
        paginate: {
          first: "First",
          last: "Last",
          next: "Next",
          previous: "Previous",
        },
        search: "Search:",
      },
      columns: [
        { data: "date" },
        {
          data: "reference_no",
          render: (data, type, row) => {
            return `<a class="underlined-link" href="${config.BASE_URL}/order_summary/print/${row.id}">${row.reference_no}</a>`;
          },
        },
        {
          data: "customer",
          render: (data) => {
            return `<span title="${data}" class="mightOverflow col-xs-1">${data}</span>`;
          },
        },
        { data: "grand_total" },
        { data: "grand_total1" },
        { data: "route_number" },
        { data: "picklist_number" },
        { data: "manifest_id" },
        { data: "trip_number" },
        { data: "tax_invoice_date" },
        {
          data: null,
          render: (data, type, row) => {
            let badgeColor = "";
            switch (row.sale_status) {
              case "Rejected":
                badgeColor = "bg-warning text-dark";
                break;
              case "Intransit":
                badgeColor = "bg-success text-white";
                break;
              case "Undelivered":
                badgeColor = "bg-danger text-white";
                break;
              default:
                badgeColor = "bg-secondary text-white";
                break;
            }
            return `<span class="badge ${badgeColor}">${row.sale_status}</span>`;
          },
        },
      ],
      columnDefs: [
        {
          orderable: false,
          targets: "_all",
        },
      ],
      pageLength: 50,
    });
  };

  // Column width handling
  const [colWidths, setColWidths] = useState([100, 120, 180, 100, 100, 100, 100, 100, 100, 120, 100]);
  const userId = localStorage.getItem("userId");
  const tableName = "salesOrderHistoryTable";
  const debouncedSetTableWidths = useRef(
    debounce((userId, tableName, widths) => {
      setTableWidths(userId, tableName, widths);
    }, 500)
  ).current;
  const startResizing = (index, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = colWidths[index];
    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(50, startWidth + moveEvent.clientX - startX);
      setColWidths((widths) => {
        const updated = [...widths];
        updated[index] = newWidth;
        debouncedSetTableWidths(userId, tableName, updated);
        return updated;
      });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    async function fetchWidths() {
      try {
        const res = await getTableWidths(userId, tableName);
        if (res?.data?.widths && Array.isArray(res.data.widths) && res.data.widths.length > 0) {
          setColWidths(res.data.widths);
        }
      } catch (e) {}
    }
    fetchWidths();
  }, []);

  // Initial load
  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    getSalesOrderList("", "", searchValue);
    return () => {
      if (salesOrderRef.current && $.fn.DataTable.isDataTable(salesOrderRef.current)) {
        $(salesOrderRef.current).DataTable().destroy();
      }
    };
  }, []);

  // Run search with debounce when searchValue changes
  const debouncedSearch = useRef(
    debounce((val) => {
      const [startDate, endDate] = salesDates;
      let formattedStartDate = startDate ? format(startDate, "MM/dd/yyyy") : "";
      let formattedEndDate = endDate ? format(endDate, "MM/dd/yyyy") : "";
      getSalesOrderList(formattedStartDate, formattedEndDate, val);
    }, 500)
  ).current;

  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue]);

  // Auto-submit on date change
  useEffect(() => {
    if (salesDates && salesDates[0] && salesDates[1]) {
      handleSubmit();
    }
  }, [salesDates]);

  return (
    <>
      <Header />
      <div className="breadcrumbs">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 col-lg-12 col-sm-12">
              <a href="#">
                <i className="fa fa-home"></i>
              </a>{" "}
              &gt; <a href="#">Dashboard</a> &gt;{" "}
              <span className="last-crumb">Sales History</span>
            </div>
          </div>
        </div>
      </div>

      <div className="main-container position-relative">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-12 col-lg-12 col-sm-12">
              <div>
                <div className="dashboard">
                  <div className="col-md-12 col-lg-12 col-xs-12">
                    <div className="row filter-row-datatable topsearch-area">
                      <div className="col-md-3">
                        <CommonDatePicker
                          value={salesDates}
                          onChange={(value) => setSalesDates(value)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div className="col-md-3 col-lg-6 col-sm-12 d-flex gap-3">
                        <span>Order Total: <span className="fw-bold">{grandTotal}</span></span>
                        <span>Current Total: <span className="fw-bold">{currentGrandTotal}</span></span>
                      </div>
                      <div className="col-md-1">Search</div>
                      <div className="col-md-2">
                        <input
                          type="text"
                          id="searchValue"
                          className="form-control"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          name="code"
                          maxLength={10}
                        />
                      </div>
                    </div>

                    <div className="verical-scrl-wrapper order-history-table" id="style-3">
                      <table
                        className="table table-bordered dataTable resizable sales-order-history-table"
                        ref={salesOrderRef}
                      >
                        <thead>
                          <tr>
                            {[
                              "Order Date",
                              "Order ID",
                              "Customers (A/C No)",
                              "Order Amt",
                              "Current Amt",
                              "Route",
                              "Picking No",
                              "Manifest No",
                              "Trip No",
                              "Tax Inv Date",
                              "Status",
                            ].map((header, idx) => (
                              <th
                                key={header}
                                style={{ width: colWidths[idx], minWidth: 50, position: "relative" }}
                              >
                                {header}
                                {idx < colWidths.length - 1 && (
                                  <div className="th-resizer" onMouseDown={(e) => startResizing(idx, e)} />
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody></tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SalesOrderHistory;
